import React, { useCallback, useEffect, useState } from "react";
import { Typography, Box, Stack, Divider } from "@mui/material";
import { LoginForm } from "components/forms/LoginForm";
import {
  LoginWithMicrosoftBtn,
  SuccessLinkLoginBlock,
  ResentLoginLinkBlock,
} from "../components/ui";
import { useSentLoginLinkMutation } from "../api";
import { getErrorMessage } from "../utils/getErrorMessage";
import { toast } from "react-toastify";
import { CustomToast } from "../components/ui/CustomToast";
import { useLocation } from "react-router-dom";
import { AppRoutes } from "../types/enums";

const LoginPage = () => {
  const [isLoginLinkSuccess, setIsLoginLinkSuccess] = useState<boolean>(false);
  const [isResentLink, setIsResentLink] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [sentLoginLink, { isLoading }] = useSentLoginLinkMutation();
  const { pathname } = useLocation();

  const isPlaybook = pathname.startsWith(AppRoutes.PLAYBOOK);

  const resentHandler = () => {
    sentLoginLink({ email, redirect_url: isPlaybook ? pathname : undefined })
      .unwrap()
      .catch((err: Error) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title={"Failed getting access link"}
          />,
        );
        setIsResentLink(false);
      });
  };

  const getLink = useCallback(() => {
    const isSuccess = localStorage.getItem("success");
    if (isSuccess) {
      setIsLoginLinkSuccess(true);
      localStorage.removeItem("success");
    }
  }, []);

  useEffect(() => {
    document.addEventListener("visibilitychange", getLink);

    return () => {
      document.removeEventListener("visibilitychange", getLink);
      localStorage.removeItem("success");
    };
  }, [getLink]);

  const successHandler = (email: string) => {
    setEmail(email);
    setIsResentLink(true);
  };

  if (isResentLink && isLoginLinkSuccess) {
    return <SuccessLinkLoginBlock />;
  }

  if (isResentLink)
    return (
      <ResentLoginLinkBlock
        email={email}
        resentHandler={resentHandler}
        isLoading={isLoading}
        backHandler={() => setIsResentLink(false)}
      />
    );

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="calc(100vh - 74px)"
    >
      <Stack width="368px" borderRadius="8px" textAlign="center" gap="16px">
        <Stack gap={1} pb={2}>
          <Typography variant="h1" component="h1" textAlign="left">
            Sign In
          </Typography>
          <Typography
            variant="subtitle1"
            textAlign="left"
            sx={{ color: "#4C545B" }}
          >
            {isPlaybook
              ? "This report is available to authorized users only. Please sign in to view it."
              : "Access with your INFUSE account or enter your email to get the one-time authorization link."}
          </Typography>
        </Stack>
        <LoginWithMicrosoftBtn />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Divider sx={{ flexGrow: 1, color: "#CFCFCF" }} />
          <Typography
            variant="button"
            sx={{
              marginX: "8px",
              color: "#4C545B",
            }}
          >
            or
          </Typography>
          <Divider sx={{ flexGrow: 1, color: "#CFCFCF" }} />
        </Box>
        <LoginForm successHandler={successHandler} />
      </Stack>
    </Box>
  );
};

export default LoginPage;
